import { Trash2 } from "lucide-react";
import { useCallback, useState } from "react";

import { ConfirmationModal } from "@/components/confirmation-modal/ConfirmationModal";

interface DeleteConfirmationCopy {
  deleteAllText: string;
  deleteAllTitle: string;
  deleteOneText: string;
  deleteOneTitle: string;
}

interface UseDeleteConfirmationsOptions<T> extends DeleteConfirmationCopy {
  onDeleteAll: () => void;
  onDeleteOne: (file: T) => void;
}

export function useDeleteConfirmations<T>({
  deleteAllText,
  deleteAllTitle,
  deleteOneText,
  deleteOneTitle,
  onDeleteAll,
  onDeleteOne,
}: UseDeleteConfirmationsOptions<T>) {
  const [pendingDeleteFile, setPendingDeleteFile] = useState<null | T>(null);
  const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false);

  const requestDelete = useCallback((file: T) => {
    setPendingDeleteFile(file);
  }, []);

  const requestDeleteAll = useCallback(() => {
    setIsDeleteAllOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteFile === null) return;
    onDeleteOne(pendingDeleteFile);
    setPendingDeleteFile(null);
  }, [onDeleteOne, pendingDeleteFile]);

  const handleConfirmDeleteAll = useCallback(() => {
    onDeleteAll();
    setIsDeleteAllOpen(false);
  }, [onDeleteAll]);

  const deleteConfirmationsModals = (
    <>
      <ConfirmationModal
        cancelButtonText="Cancel"
        confirmButtonColor="destructive"
        confirmButtonText="Delete"
        icon={<Trash2 className="text-text-error-primary" size={20} />}
        isOpen={pendingDeleteFile !== null}
        onConfirm={handleConfirmDelete}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteFile(null);
        }}
        text={deleteOneText}
        title={deleteOneTitle}
      />

      <ConfirmationModal
        cancelButtonText="Cancel"
        confirmButtonColor="destructive"
        confirmButtonText="Delete all"
        icon={<Trash2 className="text-text-error-primary" size={20} />}
        isOpen={isDeleteAllOpen}
        onConfirm={handleConfirmDeleteAll}
        onOpenChange={(open) => {
          if (!open) setIsDeleteAllOpen(false);
        }}
        text={deleteAllText}
        title={deleteAllTitle}
      />
    </>
  );

  return {
    deleteConfirmationsModals,
    requestDelete,
    requestDeleteAll,
  };
}
